import { useItemContext } from '@weenat/client'
import { denormalizeDataPoint } from '@weenat/client/dist/core/charts/charts'
import { DataPoint } from '@weenat/client/dist/core/charts/d3-charts.type'
import { getColorScalePerMetric } from '@weenat/client/dist/core/measurements'
import { fromWindAngleToDirection } from '@weenat/client/dist/core/metrics'
import { TimeSteps } from '@weenat/client/dist/enums/TimeSteps'
import { Unit } from '@weenat/client/dist/enums/UnitChoices'
import { useConvertedValue } from '@weenat/client/dist/hooks'
import { DataByDateByMetric, DataByMetric } from '@weenat/client/dist/resources/charts'
import { DeviceAvailableMeasure } from '@weenat/client/dist/resources/devices'
import { PlotAvailableMeasure } from '@weenat/client/dist/resources/plots'
import { useIntl } from '@weenat/wintl'
import { isEmpty, isNil } from 'lodash-es'
import moment from 'moment-timezone'
import { RefObject, useCallback, useMemo } from 'react'
import { useIsAppAccessRestricted } from '../hooks/useIsAppAccessRestricted'
import Axis from '../kit/d3-charts/Axis'
import Bar from '../kit/d3-charts/Bar'
import ChartWrapper from '../kit/d3-charts/ChartWrapper'
import CursorChart from '../kit/d3-charts/CursorChart'
import DefaultChartTooltipContent from '../kit/d3-charts/DefaultChartTooltipContent'
import FutureArea from '../kit/d3-charts/FutureArea'
import Line from '../kit/d3-charts/Line'
import RestrictedArea from '../kit/d3-charts/RestrictedArea'
import SolarRadiationThresholdLine from '../kit/d3-charts/SolarRadiationThresholdLine'
import TimeAxis from '../kit/d3-charts/TimeAxis'
import WindDirectionGutter, {
  WIND_DIRECTION_GUTTER_HEIGHT
} from '../kit/d3-charts/WindDirectionGutter'
import TextNumber from '../kit/TextNumber'

const CHART_HEIGHT = 196

const domainPadding = { bottom: 2, left: 4, right: 4, top: 16 }

const noNormalization = (x: number | null) => x

interface DeviceMetricChartProps {
  charRef: RefObject<HTMLDivElement>
  dataByDateByMetric: DataByDateByMetric
  dataByMetric: DataByMetric
  domain: {
    x: [Date, Date]
    y: [number, number]
  }
  isVWS?: boolean
  metric: DeviceAvailableMeasure | PlotAvailableMeasure
  timeStep: TimeSteps
  unit?: Unit
}

/**
 * Chart used on DeviceDashboard
 */
const DeviceMetricChart: FC<DeviceMetricChartProps> = ({
  chartRef,
  dataByDateByMetric,
  dataByMetric,
  domain,
  isVWS,
  metric,
  timeStep,
  unit
}) => {
  const { timezone } = useItemContext()
  const { t } = useIntl()
  const { isRestricted } = useIsAppAccessRestricted({
    isVWS
  })
  const { convertValue, formatConvertedValue } = useConvertedValue()

  const data = dataByMetric.get(metric)
  const windDirectionData = metric === 'FF' ? dataByMetric?.get('DD') : undefined
  const windGustsData = metric === 'FF' ? dataByMetric?.get('FXY') : undefined

  const denormalizeFct = useMemo(() => denormalizeDataPoint(domain.y), [domain.y])

  const ChartContent = useMemo(() => {
    switch (metric) {
      // T_DRY & T_WET are rendered in their own Chart
      case 'T':
      case 'U':
      case 'T_SOIL':
      case 'SSI':
      case 'PPFD':
      case 'LW_V':
      case 'LW_D':
      case 'T_DRY':
      case 'T_WET':
        return (
          <>
            {metric === 'SSI' ? (
              <SolarRadiationThresholdLine
                normalizeFct={noNormalization}
                denormalizeFct={noNormalization}
                variant={'sm'}
              />
            ) : null}
            <Line data={data} metric={metric} timeStep={timeStep} denormalize={denormalizeFct} />
          </>
        )

      case 'RR':
        const colorScale = getColorScalePerMetric(metric)

        return <Bar data={data} barWidth={4} $color={colorScale(domain.y)} />
      case 'FF':
        return (
          <>
            {!isNil(windDirectionData) && !isEmpty(windDirectionData) ? (
              <WindDirectionGutter data={windDirectionData} />
            ) : null}
            <Line data={data} metric={metric} timeStep={timeStep} denormalize={denormalizeFct} />
            <Line
              data={windGustsData}
              metric={'FXY'}
              timeStep={timeStep}
              denormalize={denormalizeFct}
              opacity={0.5}
            />
          </>
        )

      default:
        return null
    }
  }, [data, denormalizeFct, domain.y, metric, timeStep, windDirectionData, windGustsData])

  const renderTooltip = useCallback(
    ({ value, date }: DataPoint) => {
      const windDirValue = dataByDateByMetric.get(date.getTime())?.get('DD')?.value
      const windGustValue = dataByDateByMetric.get(date.getTime())?.get('FXY')?.value

      let formattedValue: string | null = formatConvertedValue({
        metric,
        value,
        displayUnit: true,
        showEmptyState: true,
        abbreviatedUnit: true,
        maximumFractionDigits:
          metric === 'PPFD' || metric === 'PPFD_MAX' || metric === 'PPFD_MIN'
            ? 0
            : metric === 'LW_D'
              ? 2
              : undefined
      })

      if (metric === 'LW_V') {
        formattedValue = !isNil(value)
          ? `${t(`dss.leafWetness.flags.${value < 1 ? 'dry' : 'wet'}`, {
              capitalize: true
            })} (${value})`
          : null
      }

      return (
        <DefaultChartTooltipContent>
          <TextNumber>
            {moment
              .tz(date, timezone)
              .format(timeStep === TimeSteps.perHours ? 'DD/MM HH:mm' : 'DD/MM')}
          </TextNumber>
          {metric === 'FF' ? (
            <Box>
              <Flex $alignItems='center' $gap={'md'}>
                <TextNumber $fontSize='lg' $lineHeight='lg' $fontWeight='bold' $ellipsis>
                  {formattedValue}
                </TextNumber>
                <TextNumber
                  $fontSize='lg'
                  $fontWeight='bold'
                  $lineHeight='lg'
                  $color={'grayscale.700'}
                >
                  {t(`windDirections.${fromWindAngleToDirection(windDirValue)}_abbr`, {
                    capitalize: true
                  })}
                </TextNumber>
              </Flex>
              <TextNumber
                $fontSize='sm'
                $fontWeight='medium'
                $lineHeight='sm'
                $color={'grayscale.700'}
              >
                {formatConvertedValue({
                  metric,
                  value: windGustValue,
                  displayUnit: true,
                  showEmptyState: true,
                  abbreviatedUnit: true
                })}
              </TextNumber>
            </Box>
          ) : (
            <TextNumber $fontSize='lg' $lineHeight='lg' $fontWeight='bold' $ellipsis>
              {formattedValue}
            </TextNumber>
          )}
        </DefaultChartTooltipContent>
      )
    },
    [dataByDateByMetric, formatConvertedValue, metric, t, timeStep, timezone]
  )

  return (
    <ChartWrapper height={CHART_HEIGHT} isEmpty={isEmpty(data)} isLoading={false}>
      <CursorChart
        height={CHART_HEIGHT}
        width={'100%'}
        domain={domain}
        domainPadding={domainPadding}
        data={data}
        ref={chartRef}
        timeStep={timeStep}
        marginTop={metric === 'FF' ? WIND_DIRECTION_GUTTER_HEIGHT : 12}
        marginBottom={24}
        marginRight={16}
        isChartRestricted={isRestricted}
        renderTooltip={renderTooltip}
      >
        <FutureArea />
        {ChartContent}
        <Axis
          denormalize={noNormalization}
          metric={metric}
          unit={unit}
          convertValue={convertValue}
        />
        <TimeAxis timezone={timezone} period={domain.x} />
        {isRestricted ? <RestrictedArea /> : null}
      </CursorChart>
    </ChartWrapper>
  )
}

export default DeviceMetricChart
